// src/layouts/Admin.js
import React from "react";
import {
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import PerfectScrollbar from "perfect-scrollbar";

// core components
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import FixedPlugin from "components/FixedPlugin/FixedPlugin.js";
import BottomNavigation from "components/BottomNavigation/BottomNavigation.js";

import routes from "routes.js";
import logo from "assets/img/black_icon_transparent_background.png";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";

// chart.js
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// hooks
import useFetchUserMe from "hooks/useFetchUserMe";
import useKycSession from "hooks/useKycSession";

// SweetAlert2
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import { Enviroments } from "enviroments/enviroments";

const MySwal = withReactContent(Swal);

ChartJS.register(
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

let ps;

function Admin() {
  const location = useLocation();
  const mainPanelRef = React.useRef(null);
  const history = useHistory();
  const [sidebarOpened, setsidebarOpened] = React.useState(
    document.documentElement.className.indexOf("nav-open") !== -1
  );

  // ==== Perfil (LG /me) + sincronização backend ====
  const {
    data: userMe,
    loading: loadingUser,
    error: errorUser,
    refetch,
    logout,
  } = useFetchUserMe();

  // ==== KYC ====
  const [kycApproved, setKycApproved] = React.useState(null); // null | true | false
  const { frameId, fetchKycSession } = useKycSession();
  const kycSwalOpenRef = React.useRef(false); // controla modal de KYC
  const kycStatusCheckedRef = React.useRef(false); // evita múltiplas chamadas ao legitimuzStatus

  // ==== Controle de alertas ====
  const [swalDismissed, setSwalDismissed] = React.useState(false);
  const swalOpenRef = React.useRef(false); // depósito
  const pollRef = React.useRef(null);

  // ==== Assinatura inativa (loop com limite) ====
  const [subscriptionAlertCount, setSubscriptionAlertCount] =
    React.useState(() => {
      const saved = localStorage.getItem("subscription_alert_count");
      return saved ? parseInt(saved, 10) : 0;
    });
  const subscriptionAlertRef = React.useRef(false);
  const subscriptionTimerRef = React.useRef(null);

  // ==== Helpers ====
  const getBalance = React.useCallback((me) => {
    const v = Number(me?.balance);
    return Number.isNaN(v) ? 0 : v / 100; // LG geralmente retorna em centavos
  }, []);

  const getCasinoToken = React.useCallback(() => {
    return (
      localStorage.getItem("casinoToken") ||
      localStorage.getItem("apiToken") ||
      ""
    );
  }, []);

  const casinoTokenExpired = React.useCallback(() => {
    const at = localStorage.getItem("casinoExpiresAt");
    if (!at) return false;
    try {
      return new Date(at).getTime() <= Date.now();
    } catch {
      return false;
    }
  }, []);

  // ===== KYC: Removido conforme solicitado =====
  // const checkKyc = React.useCallback(async () => { ... }, []);

  // ===== Assinatura inativa: alerta e loop controlado =====
  const checkSubscriptionStatus = React.useCallback(() => {
    const status = localStorage.getItem("subscription_status");
    if (status === "active") {
      localStorage.setItem("subscription_alert_count", "0");
      setSubscriptionAlertCount(0);
      return;
    }

    if (subscriptionAlertRef.current) return;
    subscriptionAlertRef.current = true;

    const next = subscriptionAlertCount + 1;
    localStorage.setItem("subscription_alert_count", String(next));
    setSubscriptionAlertCount(next);

    const isThird = next >= 3;

    MySwal.fire({
      title: isThird ? "Acesso Bloqueado" : "Plano Inativo",
      html: `
        <div style="text-align:center;">
          <p style="color:#A3A6AF;margin:0 0 16px;">
            ${
              isThird
                ? "Seu acesso foi bloqueado por falta de plano ativo."
                : "Seu plano não está ativo no momento."
            }
          </p>
          <p style="color:#A3A6AF;margin:0 0 20px;">
            ${
              isThird
                ? "Você será desconectado automaticamente."
                : "Entre em contato com o suporte para reativar seu acesso."
            }
          </p>
          ${
            !isThird
              ? `<p style="color:#F6E05E;font-size:12px;margin:0;">Tentativa ${next} de 3</p>`
              : ""
          }
        </div>
      `,
      icon: isThird ? "error" : "warning",
      showCancelButton: !isThird,
      confirmButtonText: isThird ? "Entendi" : "Comprar Plano",
      cancelButtonText: isThird ? undefined : "Fechar",
      allowOutsideClick: !isThird,
      allowEscapeKey: !isThird,
      background: "#121317",
      color: "#ECECF3",
      customClass: {
        popup: "swal2-aviator-popup",
        confirmButton: "swal2-aviator-confirm",
        cancelButton: "swal2-aviator-cancel",
        container: "swal2-aviator-container",
      },
      buttonsStyling: false,
      didOpen: () => {
        const el = document.querySelector(".swal2-container");
        if (el) el.style.zIndex = 2100;
      },
    }).then((r) => {
      subscriptionAlertRef.current = false;

      if (r.isConfirmed) {
        if (isThird) {
          localStorage.clear();
          window.location.href = "/auth/login";
        } else {
          window.open("https://automapro-aviator.lovable.app/", "_blank");
          localStorage.setItem("subscription_alert_count", "0");
          setSubscriptionAlertCount(0);
        }
      } else if (r.isDismissed && !isThird) {
        // re-checa em 30s
        if (subscriptionTimerRef.current)
          clearTimeout(subscriptionTimerRef.current);
        subscriptionTimerRef.current = setTimeout(() => {
          checkSubscriptionStatus();
        }, 30000);
      }
    });
  }, [subscriptionAlertCount]);

  // ===== Depósito: alerta =====
  const showDepositAlert = React.useCallback(
    (balance) => {
      if (swalOpenRef.current) return;
      swalOpenRef.current = true;

      MySwal.fire({
        title: "Depósito necessário",
        html: `
        <p style="color:#A3A6AF;margin:0 0 6px;">
          Para aproveitar o <strong>Automapro Aviator</strong> ao máximo, é necessário ter saldo na sua banca.
        </p>
        <p style="color:#A3A6AF;margin:0;">
          Realize um depósito para liberar todas as funcionalidades.
        </p>
        <div style="margin-top:12px;font-size:13px;">
          <span style="color:#A3A6AF;">Seu saldo: </span>
          <strong style="color:#F6E05E;">R$ ${Number(
            balance || 0
          ).toFixed(2)}</strong>
        </div>
      `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Depositar",
        cancelButtonText: "Fechar",
        reverseButtons: true,
        allowOutsideClick: true,
        allowEscapeKey: true,
        backdrop: true,
        background: "#121317",
        color: "#ECECF3",
        customClass: {
          popup: "swal2-aviator-popup",
          confirmButton: "swal2-aviator-confirm",
          cancelButton: "swal2-aviator-cancel",
          container: "swal2-aviator-container",
        },
        buttonsStyling: false,
        didOpen: () => {
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = 2000;
        },
      }).then((result) => {
        swalOpenRef.current = false;
        if (result.isConfirmed) {
          history.push("/admin/deposit");
        } else {
          setSwalDismissed(true);
        }
      });
    },
    [history]
  );

  // ===== Orquestração: 1) Assinatura -> 2) KYC =====
  React.useEffect(() => {
    // 0) Token da LG válido?
    if (casinoTokenExpired()) {
      logout();
      return;
    }

    // 1) Assinatura (se quiser reativar depois, é só descomentar)
    /**
    const subscriptionStatus = localStorage.getItem("subscription_status");
    if (subscriptionStatus !== "active") {
      const t = setTimeout(() => checkSubscriptionStatus(), 1200);
      return () => clearTimeout(t);
    }
    */

    // 2) KYC
    // const t2 = setTimeout(() => {
    //   checkKyc();
    // }, 600);

    // return () => clearTimeout(t2);
  }, [casinoTokenExpired, logout]);

  // ===== Efeito responsável por ABRIR o modal de KYC =====
  React.useEffect(() => {
    if (kycApproved === false && frameId && !kycSwalOpenRef.current) {
      kycSwalOpenRef.current = true;

      MySwal.fire({
        title: "Verificação KYC",
        html: `
          <div>
            <p><strong>É necessário fazer a verificação da sua conta para utilizar o aplicativo. Escaneie o documento abaixo.</strong></p>
            <p><strong>Em caso de dúvidas, clique em "SUPORTE".</strong></p>
            <a href="https://wa.me/+5511971522258?text=Preciso%20de%20Ajuda%20(ref:%20modal%20legitimuz)" target="_blank" class="btn btn-sm">Suporte</a>
            <div style="height: 70vh; margin-top: 15px;">
              <iframe 
                id="legitimuz-iframe" 
                width="100%" 
                height="100%" 
                frameborder="0"
                allow="camera;microphone;clipboard-write"
                src="https://widget.legitimuz.com/${frameId}?lang=pt"
                style="height:100%;border:none;"
              ></iframe>
            </div>
          </div>
        `,
        showCloseButton: true,
        showConfirmButton: false,
        width: "80%",
        background: "#121317",
        color: "#ECECF3",
        customClass: {
          popup: "swal2-aviator-popup",
          container: "swal2-aviator-container",
        },
        buttonsStyling: false,
        allowOutsideClick: false,
      }).then(() => {
        // se o usuário fechar manualmente, libera para reabrir depois, se necessário
        kycSwalOpenRef.current = false;
      });
    }
  }, [kycApproved, frameId]);

  // 3) Depósito (apenas se assinatura ativa E kycApproved === true)
  React.useEffect(() => {
    const onDepositPage = location.pathname?.startsWith("/admin/deposit");
    const subscriptionStatus = localStorage.getItem("subscription_status");
    const bal = getBalance(userMe);

    if (
      !loadingUser &&
      !errorUser &&
      subscriptionStatus === "active" &&
      kycApproved === true
    ) {
      if (!onDepositPage && bal <= 0 && !swalDismissed) {
        showDepositAlert(bal);
      } else if (bal > 0 && MySwal.isVisible()) {
        Swal.close();
        swalOpenRef.current = false;
      }
    }
  }, [
    userMe,
    loadingUser,
    errorUser,
    location.pathname,
    swalDismissed,
    getBalance,
    showDepositAlert,
    kycApproved,
  ]);

  // Polling: só quando a assinatura está ativa, KYC aprovado e saldo <= 0 (fora de /deposit)
  React.useEffect(() => {
    const onDepositPage = location.pathname?.startsWith("/admin/deposit");
    const subscriptionStatus = localStorage.getItem("subscription_status");
    const bal = getBalance(userMe);
    const shouldPoll =
      !loadingUser &&
      !errorUser &&
      subscriptionStatus === "active" &&
      kycApproved === true &&
      bal <= 0 &&
      !onDepositPage;

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (shouldPoll) {
      pollRef.current = setInterval(() => {
        refetch();
        setSwalDismissed(false);
      }, 120000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [
    userMe,
    loadingUser,
    errorUser,
    location.pathname,
    refetch,
    getBalance,
    kycApproved,
  ]);

  // ===== Estilos do SweetAlert =====
  const swalStyles = (
    <style>{`
      .swal2-aviator-container { z-index: 2000 !important; }
      .swal2-aviator-popup {
        border: 1px solid #26272B !important;
        border-radius: 10px !important;
        box-shadow: 0 12px 40px rgba(0,0,0,0.6) !important;
      }
      .swal2-aviator-confirm, .swal2-aviator-cancel {
        font-weight: 600;
        padding: 8px 16px;
        border-radius: 8px;
        margin: 0 6px;
        cursor: pointer;
      }
      .swal2-aviator-confirm {
        background: #913ef8 !important;
        color: #fff !important;
        border: none !important;
      }
      .swal2-aviator-confirm:hover { filter: brightness(1.05); }
      .swal2-aviator-cancel {
        background: #1a1b20 !important;
        color: #ECECF3 !important;
        border: 1px solid #26272B !important;
      }
    `}</style>
  );

  // ===== infra do template =====
  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      document.documentElement.className += " perfect-scrollbar-on";
      document.documentElement.classList.remove("perfect-scrollbar-off");
      ps = new PerfectScrollbar(mainPanelRef.current, {
        suppressScrollX: true,
      });
      let tables = document.querySelectorAll(".table-responsive");
      for (let i = 0; i < tables.length; i++)
        new PerfectScrollbar(tables[i]);
    }
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps && ps.destroy();
        document.documentElement.classList.add("perfect-scrollbar-off");
        document.documentElement.classList.remove("perfect-scrollbar-on");
      }
      if (subscriptionTimerRef.current)
        clearTimeout(subscriptionTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      let tables = document.querySelectorAll(".table-responsive");
      for (let i = 0; i < tables.length; i++)
        new PerfectScrollbar(tables[i]);
    }
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainPanelRef.current) mainPanelRef.current.scrollTop = 0;
  }, [location]);

  const toggleSidebar = () => {
    document.documentElement.classList.toggle("nav-open");
    setsidebarOpened(!sidebarOpened);
  };

  const getRoutes = (routes) =>
    routes.map((prop, key) =>
      prop.layout === "/admin" ? (
        <Route
          path={prop.layout + prop.path}
          component={prop.component}
          key={key}
        />
      ) : null
    );

  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      if (
        location.pathname.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].name;
      }
    }
    return "Brand";
  };

  return (
    <BackgroundColorContext.Consumer>
      {({ color, changeColor }) => (
        <>
          {swalStyles}
          <div className="wrapper">
            <Sidebar
              routes={routes}
              logo={{
                outterLink: "https://instragram.com/teleco.aviator",
                text: "DASHBOARD",
                imgSrc: logo,
              }}
              toggleSidebar={toggleSidebar}
            />
            <div className="main-panel" ref={mainPanelRef} data={color}>
              <AdminNavbar
                brandText={getBrandText(location.pathname)}
                toggleSidebar={toggleSidebar}
                sidebarOpened={sidebarOpened}
              />

              <Switch>
                {getRoutes(routes)}
                <Redirect from="*" to="/auth/login" />
              </Switch>

              <BottomNavigation
                routes={routes}
                onToggleSidebar={toggleSidebar}
              />

              {location.pathname === "/admin/maps" ? null : <Footer fluid />}
            </div>
          </div>
          <FixedPlugin bgColor={color} handleBgClick={changeColor} />
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
}

export default Admin;
